import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (path) => (path ? `${API}/files/${path}` : '');
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '');

const setMeta = (title, description) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
  m.content = description;
};

export function BlogList() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    setMeta('Blog | Little Splash Swim Program — Des Moines, IA', 'Water-safety tips, swim lesson advice and news from Little Splash Swim Program in Des Moines, IA.');
    fetch(`${API}/blog`).then((r) => r.json()).then((d) => setPosts(d.posts || []));
  }, []);
  return (
    <main className="py-16 md:py-20" data-testid="blog-page">
      <div className="max-w-6xl mx-auto px-6">
        <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">The Little Splash Blog</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-splash-green leading-tight">Water-safety tips &amp; stories</h1>
        <p className="mt-4 text-splash-green/75 max-w-2xl">A new post every month from Coach Abrie on swim confidence, water safety, and helping little ones love the water.</p>
        <div className="mt-12">
          {posts === null ? <Loader2 className="w-6 h-6 animate-spin text-splash-teal" /> : posts.length === 0 ? (
            <div className="rounded-3xl bg-white border border-splash-sand p-8 text-splash-green/75" data-testid="blog-empty">Our first post is coming soon — check back next month!</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group rounded-3xl bg-white border border-splash-sand overflow-hidden card-hover flex flex-col">
                  <div className="aspect-[16/10] bg-splash-creamDark overflow-hidden">
                    {p.cover_path && <img src={fileUrl(p.cover_path)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-xs text-splash-green/60">{fmt(p.published_at)}</div>
                    <h2 className="mt-1 font-display font-bold text-2xl text-splash-green">{p.title}</h2>
                    <p className="mt-2 text-sm text-splash-green/75 flex-1">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-splash-coralDark font-semibold text-sm">Read more <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    fetch(`${API}/blog/${slug}`).then((r) => (r.ok ? r.json() : Promise.reject())).then((p) => {
      setPost(p);
      setMeta(`${p.title} | Little Splash Swim Program`, p.excerpt || p.title);
    }).catch(() => setErr(true));
  }, [slug]);
  if (err) return <main className="py-24 text-center text-splash-green" data-testid="blog-post-missing">Post not found. <Link to="/blog" className="text-splash-teal font-semibold">Back to the blog</Link></main>;
  if (!post) return <main className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-splash-teal" /></main>;
  return (
    <main className="py-12 md:py-16" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-6">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-splash-teal font-semibold"><ArrowLeft className="w-4 h-4" /> All posts</Link>
        {post.cover_path && <img src={fileUrl(post.cover_path)} alt={post.title} className="mt-6 w-full aspect-[16/9] object-cover rounded-[2rem]" />}
        <div className="mt-8 text-xs text-splash-green/60">{fmt(post.published_at)}</div>
        <h1 className="mt-2 font-display font-bold text-4xl sm:text-5xl text-splash-green leading-tight">{post.title}</h1>
        <div className="blog-prose mt-8" data-testid="blog-post-body"><ReactMarkdown>{post.body}</ReactMarkdown></div>
      </article>
    </main>
  );
}
